/**
 * Dynamic authentication provider for multi-provider support.
 *
 * Routes to the appropriate provider (Auth0 or Authentik) based on user selection.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { auth0Config, getPingApiAudience } from './auth0Config';
import { AuthentikProvider, useAuthentikAuth, normalizeAuthentikUser } from './AuthentikProvider';
import { ProviderSelector } from './ProviderSelector';
import {
  AuthProviderType,
  AuthProviderConfig,
  EnabledProvidersResponse,
  getSelectedProvider,
  setSelectedProvider,
  clearSelectedProvider,
  getAuth0EnvConfig,
  getAuthentikEnvConfig,
  setAutoLoginIntent,
} from './authConfig';
import { env } from '../config/env';

// Unified auth context type
interface UnifiedAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any;
  error: any;
  provider: AuthProviderType | null;
  loginWithRedirect: (options?: any) => Promise<void>;
  logout: (options?: any) => void;
  getAccessTokenSilently: () => Promise<string>;
  switchProvider: () => void;
}

const UnifiedAuthContext = createContext<UnifiedAuthContextType | null>(null);

/**
 * Hook to access unified authentication context.
 */
export function useUnifiedAuth(): UnifiedAuthContextType {
  const context = useContext(UnifiedAuthContext);
  if (!context) {
    throw new Error('useUnifiedAuth must be used within a DynamicAuthProvider');
  }
  return context;
}

/**
 * Auth0 wrapper that adapts to unified context.
 */
function Auth0Wrapper({ children, onSwitchProvider }: { children: React.ReactNode; onSwitchProvider: () => void }) {
  const auth0 = useAuth0();

  const contextValue: UnifiedAuthContextType = {
    isAuthenticated: auth0.isAuthenticated,
    isLoading: auth0.isLoading,
    user: auth0.user,
    error: auth0.error,
    provider: 'auth0',
    loginWithRedirect: auth0.loginWithRedirect,
    logout: (options?: any) => {
      auth0.logout({
        logoutParams: {
          returnTo: options?.logoutParams?.returnTo || window.location.origin,
        },
      });
    },
    getAccessTokenSilently: async () => {
      return auth0.getAccessTokenSilently({
        authorizationParams: {
          audience: getPingApiAudience(),
        },
      });
    },
    switchProvider: onSwitchProvider,
  };

  return (
    <UnifiedAuthContext.Provider value={contextValue}>
      {children}
    </UnifiedAuthContext.Provider>
  );
}

/**
 * Authentik wrapper that adapts to unified context.
 */
function AuthentikWrapper({
  children,
  config,
  onSwitchProvider,
}: {
  children: React.ReactNode;
  config: AuthProviderConfig;
  onSwitchProvider: () => void;
}) {
  return (
    <AuthentikProvider config={config}>
      <AuthentikInnerWrapper onSwitchProvider={onSwitchProvider}>
        {children}
      </AuthentikInnerWrapper>
    </AuthentikProvider>
  );
}

function AuthentikInnerWrapper({ children, onSwitchProvider }: { children: React.ReactNode; onSwitchProvider: () => void }) {
  const authentik = useAuthentikAuth();

  const contextValue: UnifiedAuthContextType = {
    isAuthenticated: authentik.isAuthenticated,
    isLoading: authentik.isLoading,
    user: normalizeAuthentikUser(authentik.user),
    error: authentik.error,
    provider: 'authentik',
    loginWithRedirect: authentik.loginWithRedirect,
    logout: authentik.logout,
    getAccessTokenSilently: authentik.getAccessTokenSilently,
    switchProvider: onSwitchProvider,
  };

  return (
    <UnifiedAuthContext.Provider value={contextValue}>
      {children}
    </UnifiedAuthContext.Provider>
  );
}

interface DynamicAuthProviderProps {
  children: React.ReactNode;
}

/**
 * Dynamic authentication provider component.
 *
 * Manages provider selection and routes to the appropriate auth provider.
 * Shows provider selector when multiple providers are available and none is selected.
 */
export function DynamicAuthProvider({ children }: DynamicAuthProviderProps) {
  const [selectedProvider, setProvider] = useState<AuthProviderType | null>(null);
  const [enabledProviders, setEnabledProviders] = useState<EnabledProvidersResponse | null>(null);
  const [isLoadingProviders, setIsLoadingProviders] = useState(true);
  const [error] = useState<string | null>(null);
  const [showSelector, setShowSelector] = useState(false);

  // Fetch enabled providers from backend
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const apiBaseUrl = env.app.apiBaseUrl;
        const response = await fetch(`${apiBaseUrl}/api/v1/settings/auth-providers/enabled`);

        if (response.ok) {
          const data = await response.json();
          setEnabledProviders(data);
        } else {
          // Backend might not have settings endpoint yet, use env defaults
          console.log('Could not fetch providers from backend, using environment config');
        }
      } catch (err) {
        console.log('Could not fetch providers from backend, using environment config');
      } finally {
        setIsLoadingProviders(false);
      }
    };

    fetchProviders();
  }, []);

  // Determine selected provider on mount
  useEffect(() => {
    if (isLoadingProviders) return;

    const stored = getSelectedProvider();
    if (stored) {
      setProvider(stored);
      return;
    }

    // Check how many providers are available
    const auth0Config = getAuth0EnvConfig();
    const authentikConfig = getAuthentikEnvConfig();

    const availableProviders: AuthProviderType[] = [];
    if (auth0Config.enabled) availableProviders.push('auth0');
    if (authentikConfig?.enabled) availableProviders.push('authentik');

    // If only Auth0 (default behavior), auto-select it
    if (availableProviders.length <= 1) {
      setProvider('auth0');
      setSelectedProvider('auth0');
    } else {
      // Multiple providers available, show selector
      setShowSelector(true);
    }
  }, [isLoadingProviders, enabledProviders]);

  const handleProviderSelected = useCallback((provider: AuthProviderType) => {
    // Remember the explicit choice so single-provider returns skip the selector,
    // and flag intent so the login screen auto-starts this provider's login.
    setSelectedProvider(provider);
    setAutoLoginIntent();
    setProvider(provider);
    setShowSelector(false);
  }, []);

  const handleSwitchProvider = useCallback(() => {
    clearSelectedProvider();
    setProvider(null);
    setShowSelector(true);
    // Force reload to clear any auth state
    window.location.href = '/';
  }, []);

  // Loading state while determining provider
  if (isLoadingProviders || (!selectedProvider && !showSelector)) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Initializing authentication...</p>
        </div>
      </div>
    );
  }

  // Show provider selector if needed
  if (showSelector) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Ping Pong Feedback
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              User Feedback Management
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <ProviderSelector
              onProviderSelected={handleProviderSelected}
              enabledProviders={enabledProviders}
              isLoading={isLoadingProviders}
              error={error}
            />
          </div>
        </div>
      </div>
    );
  }

  // Route to appropriate provider
  if (selectedProvider === 'authentik') {
    const config = getAuthentikEnvConfig();
    if (!config) {
      // Fallback to Auth0 if Authentik not configured
      return (
        <Auth0Provider {...auth0Config}>
          <Auth0Wrapper onSwitchProvider={handleSwitchProvider}>
            {children}
          </Auth0Wrapper>
        </Auth0Provider>
      );
    }

    return (
      <AuthentikWrapper config={config} onSwitchProvider={handleSwitchProvider}>
        {children}
      </AuthentikWrapper>
    );
  }

  // Default: Auth0
  return (
    <Auth0Provider {...auth0Config}>
      <Auth0Wrapper onSwitchProvider={handleSwitchProvider}>
        {children}
      </Auth0Wrapper>
    </Auth0Provider>
  );
}

export default DynamicAuthProvider;
